/**
 * Auto Login Composable
 * 自動登入：退背景／切 Tab 時把帳密（加密）存入 cookie；回前景且 token 已過期時，
 * 用 cookie 內的帳密無感重新登入，避免被登出。
 *
 * 架構：
 * - 單例模式：使用 nuxtApp._autoLoginContext 避免 useCookie 重複創建
 * - crypto.subtle 僅 client 可用；所有加解密均 client-only 且為非同步
 * - 登入時預先加密並暫存記憶體，讓 hidden 事件能「同步」寫入 cookie
 *
 * 帳密 cookie 為混淆等級加密，安全取捨見 docs/spec/auto-login.md。
 */

import { useCookie, useRuntimeConfig, useNuxtApp } from '#app'
import { deriveKey, encryptJSON, decryptJSON } from '@/utils/credentialCrypto'

const CRED_SECONDS = 60 * 60 * 24 * 7 // 帳密 cookie 效期 7 天
const PASSWORD_PROVIDERS = ['local', 'phone', 'email']

export const useAutoLogin = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 useCookie 只創建一次
  if (!nuxtApp._autoLoginContext) {
    const { clientId, appSecret } = useRuntimeConfig().public
    const isProduction = import.meta.env.MODE === 'production'

    const credCookie = useCookie(`cuora_${clientId}-cred`, {
      default: () => null,
      maxAge: CRED_SECONDS,
      secure: isProduction,
      sameSite: 'lax',
    })

    // request-scoped 狀態：預先加密的密文與衍生金鑰
    const state = { cipher: null, keyPromise: null }

    const getKey = () => {
      if (!state.keyPromise) {
        state.keyPromise = deriveKey(appSecret)
      }
      return state.keyPromise
    }

    // 從 loginData 取出可重放的帳密欄位（僅密碼類 provider）
    const pickCredentials = (loginData) => {
      if (!loginData || !PASSWORD_PROVIDERS.includes(loginData.provider) || !loginData.password) {
        return null
      }
      const { provider, password } = loginData
      if (provider === 'local') {
        return { provider, username: loginData.username, password }
      }
      if (provider === 'phone') {
        return { provider, countryCode: loginData.countryCode, phone: loginData.phone, password }
      }
      return { provider, email: loginData.email, password }
    }

    // 登入成功後呼叫：預先加密帳密存記憶體，供 hidden 事件同步寫 cookie
    const captureCredentials = async (loginData) => {
      if (!import.meta.client) {
        return
      }
      const creds = pickCredentials(loginData)
      if (!creds) {
        state.cipher = null
        return
      }
      try {
        const key = await getKey()
        state.cipher = await encryptJSON(creds, key)
      } catch (error) {
        console.error('[useAutoLogin] captureCredentials error:', error)
        state.cipher = null
      }
    }

    // visibilitychange→hidden / pagehide 時呼叫：同步把密文寫入 cookie
    const saveCredentialCookie = () => {
      if (!import.meta.client || !state.cipher) {
        return
      }
      credCookie.value = state.cipher
    }

    // 回前景偵測 token 過期時呼叫：讀取並解密帳密
    const readCredentials = async () => {
      if (!import.meta.client || !credCookie.value) {
        return null
      }
      try {
        const key = await getKey()
        return await decryptJSON(credCookie.value, key)
      } catch (error) {
        console.error('[useAutoLogin] readCredentials error:', error)
        return null
      }
    }

    const hasStoredCredentials = () => !!credCookie.value

    const clearCredentials = () => {
      credCookie.value = null
      state.cipher = null
    }

    // 只清持久化的 cred cookie、保留記憶體密文 state.cipher：
    // 供自動登入「成功」後呼叫，下次退背景仍能用 state.cipher 重新寫回 cookie（logout 才用 clearCredentials 全清）
    const clearCredentialCookie = () => {
      credCookie.value = null
    }

    // cleanup 方法：供 SSR cleanup plugin 調用（無 timer/watch，僅清記憶體）
    const cleanup = () => {
      state.cipher = null
      state.keyPromise = null
    }

    nuxtApp._autoLoginContext = {
      captureCredentials,
      saveCredentialCookie,
      readCredentials,
      hasStoredCredentials,
      clearCredentials,
      clearCredentialCookie,
      cleanup,
    }
  }

  return nuxtApp._autoLoginContext
}
